// src/components/common/PasswordInput.tsx
import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  required?: boolean;
  error?: string;
  showStrength?: boolean;
  placeholder?: string;
  className?: string;
}

export const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  onChange,
  label,
  required = false,
  error,
  showStrength = true,
  placeholder = '',
  className = '',
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const checkPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return Math.min(strength, 4);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    if (showStrength) {
      setPasswordStrength(checkPasswordStrength(newValue));
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={handleChange}
          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10 ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={placeholder}
          required={required}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 p-2 flex items-center"
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeOff className="h-5 w-5 text-gray-400" />
          ) : (
            <Eye className="h-5 w-5 text-gray-400" />
          )}
        </button>
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {showStrength && value && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Password strength:</span>
            <span className="text-xs font-medium">
              {['Weak', 'Fair', 'Good', 'Strong'][passwordStrength - 1] || 'Very Weak'}
            </span>
          </div>
          <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full ${
                passwordStrength === 1 ? 'bg-red-500' :
                passwordStrength === 2 ? 'bg-yellow-500' :
                passwordStrength === 3 ? 'bg-blue-500' :
                passwordStrength === 4 ? 'bg-green-500' : 'bg-gray-200'
              }`}
              style={{ width: `${(passwordStrength / 4) * 100}%` }}
            />
          </div>
          <ul className="text-xs text-gray-500 mt-2 space-y-1">
            <li className={value.length >= 8 ? 'text-green-600' : ''}>
              ✓ At least 8 characters
            </li>
            <li className={/[A-Z]/.test(value) ? 'text-green-600' : ''}>
              ✓ Uppercase letter
            </li>
            <li className={/[0-9]/.test(value) ? 'text-green-600' : ''}>
              ✓ Number
            </li>
            <li className={/[^A-Za-z0-9]/.test(value) ? 'text-green-600' : ''}>
              ✓ Special character
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};